/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata';
import type { GroupedPlugins } from '../groupByInstallPackage/groupByInstallPackage';
import { requiresUsersIntegrationPlugin } from '../requiresUsersIntegrationPlugin/requiresUsersIntegrationPlugin';
import {
	requiresCollaborativeFeatureIntegrationPlugin
} from '../requiresCollaborativeFeatureIntegrationPlugin/requiresCollaborativeFeatureIntegrationPlugin';
import { requiresCommentsContainer } from '../requiresCommentsContainer/requiresCommentsContainer';
import { requiresDocumentOutlineContainer } from '../requiresDocumentOutlineContainer/requiresDocumentOutlineContainer';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';

export type PluginsImportResult = {
	importsFromCKEditor: Array<string>;
	importsFromCKEditorPremiumFeatures: Array<string>;
};

export function getPluginsImport( editorType: EditorType, groupedPlugins: GroupedPlugins ): PluginsImportResult {
	const freePlugins = groupedPlugins.ckeditor5;
	const premiumPlugins = groupedPlugins[ 'ckeditor5-premium-features' ];
	const allPlugins = [ ...freePlugins, ...premiumPlugins ];

	const requiresToggleSidebarPlugins =
		isDecoupledEditor( editorType ) &&
		( requiresCommentsContainer( allPlugins ) || requiresDocumentOutlineContainer( allPlugins ) );

	const requiresCorePluginImport =
		requiresUsersIntegrationPlugin( premiumPlugins ) ||
		requiresCollaborativeFeatureIntegrationPlugin( premiumPlugins, 'Comments' ) ||
		requiresCollaborativeFeatureIntegrationPlugin( premiumPlugins, 'TrackChanges' ) ||
		requiresCollaborativeFeatureIntegrationPlugin( premiumPlugins, 'RevisionHistory' ) ||
		requiresToggleSidebarPlugins;

	const importsFromCKEditor = [
		editorType,
		requiresCorePluginImport ? 'Plugin' : '',
		requiresToggleSidebarPlugins ? 'ButtonView' : '',
		...freePlugins
	].filter( Boolean );

	const importsFromCKEditorPremiumFeatures = [
		allPlugins.includes( 'ExportInlineStyles' ) ? 'getEmailInlineStylesTransformations' : '',
		...premiumPlugins
	].filter( Boolean );

	return {
		importsFromCKEditor,
		importsFromCKEditorPremiumFeatures
	};
}
